import { ref } from 'vue'

type ScrollBehaviorOption = ScrollBehavior
type ScrollBlockPosition = ScrollLogicalPosition

export interface UseScrollPositionOptions {
  pageId: string;
  trackSelector: string; // e.g. 'li[id^="pl-"]' or 'div[id^="search-"]'
  restoreOnHistory?: boolean;
  scrollBehavior?: ScrollBehaviorOption;
  blockPosition?: ScrollBlockPosition;
}

export function useScrollPosition(options: UseScrollPositionOptions) {
  const {
    pageId,
    trackSelector,
    restoreOnHistory = true,
    scrollBehavior = 'auto',
    blockPosition = 'center',
  } = options

  const isFromHistoryNavigation = ref(false)
  const isBackToHistory = ref(false)

  function detectHistoryNavigation(): boolean {
    if (typeof window === 'undefined') return false
    // legacy
    // @ts-ignore - performance.navigation is deprecated but still present in some browsers
    if (performance && performance.navigation && performance.navigation.TYPE_BACK_FORWARD !== undefined) {
      // @ts-ignore
      return performance.navigation.type === performance.navigation.TYPE_BACK_FORWARD
    }
    const entries = performance.getEntriesByType?.('navigation') as PerformanceNavigationTiming[] | undefined
    if (entries && entries.length > 0) {
      return entries[0].type === 'back_forward'
    }
    return false
  }

 function saveScrollState(clickedElementId?: string) {
    if (typeof window === 'undefined') return
    try {
      sessionStorage.setItem(`${pageId}_scrollPosition`, String(window.scrollY))
      if (clickedElementId) sessionStorage.setItem(`${pageId}_lastClickedId`, clickedElementId)
    } catch {}
  }

  function restoreScrollState() {
    if (typeof window === 'undefined') return
    try {
      const lastClickedId = sessionStorage.getItem(`${pageId}_lastClickedId`)
      const saved = sessionStorage.getItem(`${pageId}_scrollPosition`)
      const fallbackY = saved ? parseInt(saved, 10) : 0

      const start = Date.now()
      const timeoutMs = 1000
      const attempt = () => {
        let didScroll = false
        if (lastClickedId) {
          const el = document.getElementById(lastClickedId)
          if (el) {
            el.scrollIntoView({ behavior: scrollBehavior, block: blockPosition })
            didScroll = true
            try { sessionStorage.removeItem(`${pageId}_lastClickedId`) } catch {}
          }
        }
        if (!didScroll && Date.now() - start < timeoutMs) {
          requestAnimationFrame(attempt)
          return
        }
        if (!didScroll && fallbackY >= 0) {
          window.scrollTo({ top: fallbackY, behavior: scrollBehavior })
        }
      }
      attempt()
    } catch {}
  }

  function setupClickTracking(container: HTMLElement) {
    const handleClick = (event: Event) => {
      const target = event.target as HTMLElement | null
      if (!target) return
      const tracked = target.closest(trackSelector) as HTMLElement | null
      if (tracked?.id) saveScrollState(tracked.id)
    }
    container.addEventListener('click', handleClick, true)
    return () => container.removeEventListener('click', handleClick, true)
  }

  function initScrollPosition(container: HTMLElement) {
    isFromHistoryNavigation.value = detectHistoryNavigation()

    const cleanupClick = setupClickTracking(container)

    const onPopState = () => {
      isBackToHistory.value = true
      if (restoreOnHistory) setTimeout(() => restoreScrollState(), 100)
    }
    if (restoreOnHistory) window.addEventListener('popstate', onPopState)

    if (restoreOnHistory && isFromHistoryNavigation.value) {
      setTimeout(() => restoreScrollState(), 100)
    }

    return () => {
      cleanupClick()
      if (restoreOnHistory) window.removeEventListener('popstate', onPopState)
    }
  }

  function cleanup() {
    if (typeof window === 'undefined') return
    try { sessionStorage.setItem(`${pageId}_scrollPosition`, String(window.scrollY)) } catch {}
  }

  return {
    isFromHistoryNavigation,
    isBackToHistory,
    initScrollPosition,
    restoreScrollState,
    cleanup,
  }
}


